import React, { useState } from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";

import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

/*
  This component requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/

export const query = graphql`
  fragment BlogThreeColumnCardsWithPaginationArticle on StrapiArticle {
    id
    title
    locale
    url
    date
    categories {
      url
      title
    }
    author {
      url
      name
      image {
        ...StrapiMediaSubquery
      }
    }
    readingTimeMinutes
    description
    descriptionShort
    headImage {
      ...StrapiMediaSubquery
    }
  }

  fragment BlogThreeColumnCardsWithPaginationData on StrapiComponentBlogThreeColumnCardsWithPagination {
    topic
    title
    description
    readingTimeText
    noPostsFoundText
    noPostsFoundImage {
      ...StrapiMediaSubquery
    }
    paginationPrevious
    paginationNext
    paginationCurrentPageText
  }
`;

const articlePerPage = 6;
const descriptionMaxWordCount = 50;

function limitTextByWordCount(text, wordMaxCount) {
  let wordSplits = text.split(" ");
  let wordCount = wordSplits?.length;
  if (wordCount > wordMaxCount) {
    wordSplits = wordSplits.slice(0, wordMaxCount);
    return wordSplits.join(" ") + "...";
  } else {
    return text;
  }
}

function ThreeColumnCardsWithPagination({ data, articles = [] }) {
  if (!data) {
    return <></>;
  }

  const [currentPage, setCurrentPage] = useState(1);

  let totalPages = 0;

  function increasePage() {
    goToPage(currentPage + 1);
  }

  function decreasePage() {
    goToPage(currentPage - 1);
  }

  function goToPage(page) {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      document.getElementById("divFirst").scrollIntoView();
    }
  }

  function isPageActive(i) {
    return currentPage === i;
  }

  function isFirstPage() {
    return currentPage === 1;
  }

  function isLastPage() {
    return currentPage === totalPages;
  }

  let posts = [...articles];

  totalPages = Math.ceil(posts.length / articlePerPage);

  let pageLinks = [];
  for (let i = 1; i <= totalPages; i++) {
    pageLinks.push(i);
  }
  let pageLinksLeft = null;
  let pageLinksMiddle = pageLinks;
  let pageLinksRight = null;
  if (totalPages >= 9) {
    if (currentPage > 4) pageLinksLeft = [1];

    let pageLinksMiddleStart =
      currentPage <= 4
        ? 0
        : currentPage >= totalPages - 3
        ? totalPages - 5
        : currentPage - 2;
    let pageLinksMiddleEnd =
      currentPage <= 4
        ? 5
        : currentPage >= totalPages - 3
        ? totalPages
        : currentPage + 1;
    pageLinksMiddle = pageLinks.slice(pageLinksMiddleStart, pageLinksMiddleEnd);

    if (currentPage < totalPages - 3) pageLinksRight = [totalPages];
  }
  posts = posts.slice(
    articlePerPage * (currentPage - 1),
    articlePerPage * (currentPage - 1) + articlePerPage
  );

  return (
    <div className="mx-auto max-w-7xl bg-background-white px-4 pb-20 pt-16 lg:px-6 lg:pb-28 lg:pt-24">
      <div id="divFirst" className="text-center">
        {data.topic && (
          <h3 className="block text-center text-base font-semibold uppercase tracking-wide text-text-primary">
            <MarkdownWrapper text={data.topic} />
          </h3>
        )}
        {data.title && (
          <h2 className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl">
            <MarkdownWrapper text={data.title} />
          </h2>
        )}
        {data.description && (
          <MarkdownWrapper
            text={data.description}
            markdownClassName="mt-3 max-w-2xl mx-auto text-xl text-text-secondary sm:mt-4"
            replaceParagraph={false}
          />
        )}
      </div>

      {posts?.length ? (
        <div className="mx-auto mt-8 grid max-w-lg gap-5 lg:max-w-none lg:grid-cols-3">
          {posts.map((article, index) => (
            <div
              key={index}
              className="flex flex-col overflow-hidden rounded-md bg-background-white shadow-lg hover:bg-background-secondary-light"
            >
              {article.headImage && (
                <LinkWrapper to={article.url} className="flex-shrink-0">
                  <MediaWrapper
                    media={article.headImage}
                    divClassName="w-full aspect-[1/1]"
                    className="h-full w-full"
                  />
                </LinkWrapper>
              )}
              <div className="flex h-full flex-col justify-between bg-background-transparent p-4">
                <div className="flex flex-shrink-0 flex-col">
                  {article.categories &&
                    article.categories.length &&
                    article.categories[0].title && (
                      <div className="flex">
                        <LinkWrapper
                          to={article.categories[0].url}
                          className="rounded-md px-2 hover:underline"
                        >
                          <div className="block">
                            <MarkdownWrapper
                              text={article.categories[0].title}
                              markdownClassName="text-sm font-medium text-text-secondary"
                            />
                          </div>
                        </LinkWrapper>
                      </div>
                    )}
                  {(article.title ||
                    article.descriptionShort ||
                    article.description) && (
                    <LinkWrapper
                      to={article.url}
                      className="mt-2 rounded-md px-2 hover:underline"
                    >
                      {article.title && (
                        <MarkdownWrapper
                          text={article.title}
                          markdownClassName="custom-font text-xl font-semibold text-text-primary"
                        />
                      )}
                      {(article.descriptionShort || article.description) && (
                        <MarkdownWrapper
                          text={
                            article.descriptionShort ||
                            limitTextByWordCount(
                              article.description,
                              descriptionMaxWordCount
                            )
                          }
                          markdownClassName="mt-3 text-base text-text-secondary"
                          replaceParagraph={false}
                        />
                      )}
                    </LinkWrapper>
                  )}
                </div>
                <div className="mt-6 flex items-center">
                  {article.author?.name && (
                    <div className="flex-shrink-0">
                      <LinkWrapper to={article.author.url}>
                        <span className="sr-only">{article.author.name}</span>
                        {article.author.image && (
                          <MediaWrapper
                            media={article.author.image}
                            divClassName="h-10 w-10"
                            className="rounded-full"
                          />
                        )}
                      </LinkWrapper>
                    </div>
                  )}
                  <div className="ml-3">
                    {article.author?.name && (
                      <div className="flex">
                        <LinkWrapper
                          to={article.author.url}
                          className="-ml-2 rounded-md px-2 hover:underline"
                        >
                          <MarkdownWrapper
                            text={article.author.name}
                            markdownClassName="text-sm font-medium text-text-primary"
                          />
                        </LinkWrapper>
                      </div>
                    )}
                    <div className="flex space-x-1 text-sm text-text-secondary">
                      {article.date && article.locale && (
                        <time
                          dateTime={new Date(article.date).toLocaleDateString(
                            article.locale,
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )}
                        >
                          {new Date(article.date).toLocaleDateString(
                            article.locale,
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )}
                        </time>
                      )}
                      {data.readingTimeText && article.readingTimeMinutes && (
                        <span>
                          {article.date && article.locale && (
                            <span aria-hidden="true" className="px-1">
                              &middot;
                            </span>
                          )}
                          <span>{`${article.readingTimeMinutes} ${data.readingTimeText}`}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mx-auto mt-12 flex max-w-lg justify-center lg:max-w-none">
          {data.noPostsFoundImage ? (
            <>
              <span className="sr-only">
                {data.noPostsFoundText || "No Posts Found"}
              </span>
              <MediaWrapper media={data.noPostsFoundImage} />
            </>
          ) : (
            <span className="text-center text-lg font-bold">
              {data.noPostsFoundText || "No Posts Found"}
            </span>
          )}
        </div>
      )}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-between bg-white px-4">
          <div className="flex flex-1 items-center justify-between sm:hidden">
            <button
              onClick={decreasePage}
              className={`inline-flex items-center rounded-md border border-border-light-gray bg-background-white px-4 py-2 text-sm font-medium text-text-gray hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-dark-gray ${
                isFirstPage(currentPage) ? "cursor-default opacity-0" : ""
              }`}
              disabled={isFirstPage(currentPage)}
            >
              {data.paginationPrevious ? data.paginationPrevious : "Previous"}
            </button>
            <span className="px-4 py-2 text-sm font-medium text-text-gray">{`${
              data.paginationCurrentPageText
                ? data.paginationCurrentPageText
                : "Page"
            } ${currentPage}/${totalPages}`}</span>
            <button
              onClick={increasePage}
              className={`ml-3 inline-flex items-center rounded-md border border-border-light-gray bg-background-white px-4 py-2 text-sm font-medium text-text-gray hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-dark-gray ${
                isLastPage(currentPage) ? "cursor-default opacity-0" : ""
              }`}
              disabled={isLastPage(currentPage)}
            >
              {data.paginationNext ? data.paginationNext : "Next"}
            </button>
          </div>
          <div className="hidden items-center justify-center sm:flex sm:flex-1">
            <div
              className="inline-flex bg-background-white text-center text-sm font-medium text-text-primary shadow-sm"
              aria-label="Pagination"
            >
              <button
                onClick={decreasePage}
                className={`inline-flex items-center justify-center rounded-l-md border border-border-light-gray px-2 py-2 ${
                  isFirstPage(currentPage)
                    ? "cursor-default text-text-light-gray"
                    : "hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                }`}
                disabled={isFirstPage(currentPage)}
              >
                <span className="sr-only">
                  {data.paginationPrevious
                    ? data.paginationPrevious
                    : "Previous"}
                </span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              {Boolean(pageLinksLeft?.length) && (
                <>
                  {pageLinksLeft.map((pageLink, index) => (
                    <button
                      key={index}
                      onClick={() => goToPage(pageLink)}
                      aria-current="page"
                      className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2 hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                    >
                      {pageLink}
                    </button>
                  ))}
                  <span className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2">
                    ...
                  </span>
                </>
              )}
              {Boolean(pageLinksMiddle?.length) &&
                pageLinksMiddle.map((pageLink, index) => (
                  <button
                    key={index}
                    onClick={() => goToPage(pageLink)}
                    aria-current="page"
                    className={`inline-flex w-10 items-center justify-center px-4 py-2 ${
                      isPageActive(pageLink)
                        ? "z-10 cursor-default bg-background-primary text-text-on-primary-light"
                        : "border border-border-light-gray hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                    }`}
                    disabled={isPageActive(pageLink)}
                  >
                    {pageLink}
                  </button>
                ))}
              {Boolean(pageLinksRight?.length) && (
                <>
                  <span className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2">
                    ...
                  </span>
                  {pageLinksRight.map((pageLink, index) => (
                    <button
                      key={index}
                      onClick={() => goToPage(pageLink)}
                      aria-current="page"
                      className="inline-flex w-10 items-center justify-center border border-border-light-gray px-4 py-2 hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                    >
                      {pageLink}
                    </button>
                  ))}
                </>
              )}
              <button
                onClick={increasePage}
                className={`inline-flex items-center justify-center rounded-r-md border border-border-light-gray px-2 py-2 ${
                  isLastPage(currentPage)
                    ? "cursor-default text-text-light-gray"
                    : "hover:border-border-dark-gray hover:bg-background-light-gray hover:text-text-primary-dark"
                }`}
                disabled={isLastPage(currentPage)}
              >
                <span className="sr-only">
                  {data.paginationNext ? data.paginationNext : "Next"}
                </span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ThreeColumnCardsWithPagination.propTypes = {
  data: PropTypes.object,
  articles: PropTypes.array,
};

export default ThreeColumnCardsWithPagination;
